import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

// localStorage key for the user's cookie consent.
// Value: "all" (technical + statistical) or "necessary_only" (technical only).
// No personal data stored, only the consent choice + date.
// CONSENT_VERSION bumped to 2 after switching to Google Consent Mode v2:
// older stored choices are considered expired and the banner is shown again.
const STORAGE_KEY = "scudononni_cookie_consent";
const CONSENT_VERSION = 2;

function broadcastConsent(value) {
  // GoogleAnalytics.jsx (and any other consent-aware component) listens
  // for this event to load/unload its scripts in real time.
  window.dispatchEvent(
    new CustomEvent("scudononni:cookie-consent", { detail: { value } })
  );
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Global helper so "Modifica preferenze cookie" in the footer can reopen it.
    window.scudononniOpenCookieBanner = () => setVisible(true);
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    } catch {
      stored = null;
    }
    // Show the banner if no choice yet OR the choice predates Consent Mode v2.
    if (!stored || stored.version !== CONSENT_VERSION) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const persist = (value) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ value, version: CONSENT_VERSION, date: new Date().toISOString() })
    );
    broadcastConsent(value);
    setVisible(false);
  };

  const acceptAll = () => persist("all");
  const acceptNecessary = () => persist("necessary_only");

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Informativa sui cookie"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-6 lg:left-auto lg:right-6 lg:w-[520px] z-[60] animate-fade-up"
    >
      <div className="bg-white rounded-3xl shadow-2xl border-4 border-[#1E3A8A] p-6 md:p-7">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#DBEAFE] flex items-center justify-center text-[#1E3A8A] flex-shrink-0">
            <Cookie size={26} />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-xl font-bold text-[#1E3A8A] mb-2">
              I tuoi cookie, le tue scelte
            </h3>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed">
              Usiamo cookie <strong>tecnici</strong> per far funzionare il sito (contare le verifiche giornaliere)
              e, se acconsenti, cookie <strong>statistici anonimi</strong> di Google Analytics per capire
              quante persone aiutiamo. <strong>Niente pubblicità, niente profilazione</strong>.{" "}
              <Link
                to="/cookie-policy"
                data-testid="cookie-banner-link"
                className="text-[#1E3A8A] font-bold underline"
              >
                Leggi di più
              </Link>
            </p>
          </div>
          <button
            type="button"
            data-testid="cookie-banner-close"
            onClick={acceptNecessary}
            aria-label="Chiudi e accetta solo i cookie necessari"
            className="text-gray-400 hover:text-gray-700 p-1 -mt-1 -mr-1"
          >
            <X size={22} />
          </button>
        </div>
        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            data-testid="cookie-banner-necessary"
            onClick={acceptNecessary}
            className="flex-1 border-4 border-[#1E3A8A] text-[#1E3A8A] bg-white hover:bg-blue-50 min-h-[52px] px-5 py-3 rounded-2xl transition-all active:scale-95 text-base md:text-lg font-bold"
          >
            Solo necessari
          </button>
          <button
            type="button"
            data-testid="cookie-banner-accept"
            onClick={acceptAll}
            className="flex-1 bg-[#1E3A8A] hover:bg-[#152C69] text-white min-h-[52px] px-5 py-3 rounded-2xl shadow-md hover:shadow-lg transition-all active:scale-95 text-base md:text-lg font-bold"
          >
            Accetta tutti
          </button>
        </div>
      </div>
    </div>
  );
}
